// This file contains common/shared legend model code.
// See the `components` package for legend model code specific to the Legend
// component and the  `plugin-system` package for legend model code specific to
// panel plugin specs.

import { z } from 'zod/v4';
import { aggregationsSchema } from './aggregations';

export const legendPositionsSchema = z.enum(['Bottom', 'Right']);
export type LegendPositions = z.infer<typeof legendPositionsSchema>;

export const legendModeSchema = z.enum(['List', 'Table']);
export type LegendMode = z.infer<typeof legendModeSchema>;

export const legendSizesSchema = z.enum(['Small', 'Medium']);
export type LegendSizeSpec = z.infer<typeof legendSizesSchema>;

/**
 * Standard, string-based legend sizes (e.g. Small, Medium).
 *
 * Note: Allow "Large" only on the lower-level legend component so it can be used in the expanded panel dialog.
 * It is not valid in the dashboard spec since panels are often too small, see PR #42088 for more info.
 */
export type LegendSizeStandard = LegendSizeSpec | 'Large';

/**
 * Legend size that allows the following custom options:
 * - `number`: A numeric option that will be used to set a pixel-based size for the legend.
 *   This impacts the legend height for `Bottom` positioned legends and the legend width for
 *   `Right` positioned legends.
 * - `${number}%`: A string with a number and a percentage. Will be used to set the size based on
 *   a percentage of the overall content dimensions. This impacts the legend height for `Bottom`
 *   positioned legends and the legend width for `Right` positioned legends.
 *
 * Note that `Bottom` positioned legends use these custom size values as a maximum and may render the legend
 * smaller in the following cases:
 * - If all legend items can be rendered in a smaller height, the smaller height will be used.
 * - If all legend items cannot be rendered in the specified height, the height of the legend will be adjusted to ensure
 *   the last item rendered shows half a row, so the user understands they can scroll.
 */
export type LegendSizeCustom = number | `${number}%`;

/**
 * Legend size that includes both standard and custom options.
 */
export type LegendSize = LegendSizeStandard | LegendSizeCustom;

// Common legend options used across some UI components and panel specifications
const legendOptionsBaseSchema = z.object({
  position: legendPositionsSchema,
  mode: legendModeSchema.optional(),
  size: legendSizesSchema.optional(),
});

export interface LegendOptionsBase extends z.TypeOf<typeof legendOptionsBaseSchema> {}

export function isValidLegendPosition(position: LegendPositions) {
  return legendPositionsSchema.options.includes(position);
}

export function isValidLegendMode(mode: LegendMode) {
  return legendModeSchema.options.includes(mode);
}

/**
 * Validates if the legend size is valid for spec-based use cases, which are more constrained
 * than the viz use cases.
 */
export function isValidLegendSpecSize(size: LegendSize) {
  return legendSizesSchema.options.includes(size as LegendSizeSpec);
}

/**
 * Validates if the legend size is valid for viz use cases.
 */
export function isValidLegendSize(size: LegendSize) {
  return (
    isValidLegendSpecSize(size) ||
    size === 'Large' ||
    typeof size === 'number' ||
    (typeof size === 'string' && size.endsWith('%'))
  );
}

export const DEFAULT_LEGEND: Required<LegendOptionsBase> = {
  position: 'Bottom',
  mode: 'List',
  size: 'Small',
};

export function getLegendPosition(position?: LegendPositions) {
  if (position === undefined) {
    return DEFAULT_LEGEND.position;
  }
  if (isValidLegendPosition(position)) {
    return position;
  }
  return DEFAULT_LEGEND.position;
}

export function getLegendMode(mode?: LegendMode) {
  if (!mode || !isValidLegendMode(mode)) {
    return DEFAULT_LEGEND.mode;
  }

  return mode;
}

export function getLegendSize(size?: LegendSizeSpec) {
  if (!size || !isValidLegendSize(size)) {
    return DEFAULT_LEGEND.size;
  }

  return size;
}

export type LegendSingleSelectConfig = {
  label: string;
  description?: string;
};

export const LEGEND_POSITIONS_CONFIG: Readonly<Record<LegendPositions, LegendSingleSelectConfig>> = {
  Bottom: { label: 'Bottom' },
  Right: { label: 'Right' },
};

export const LEGEND_MODE_CONFIG: Readonly<Record<LegendMode, LegendSingleSelectConfig>> = {
  List: { label: 'List' },
  Table: { label: 'Table' },
};

export const LEGEND_SIZE_CONFIG: Readonly<Record<LegendSizeSpec, LegendSingleSelectConfig>> = {
  Small: { label: 'Small' },
  Medium: { label: 'Medium' },
};

/**
 * Validates the legend spec (i.e. DashboardPanel use case).
 */
export function validateLegendSpec(legend?: LegendOptionsBase) {
  if (legend === undefined) {
    // undefined is valid since this is how legend is hidden by default
    return true;
  }
  if (!isValidLegendPosition(legend.position)) {
    return false;
  }
  if (legend.mode && !isValidLegendMode(legend.mode)) {
    return false;
  }
  if (legend.size && !isValidLegendSpecSize(legend.size)) {
    return false;
  }

  return true;
}

/**
 * Validates the legend props (i.e. Viz use case).
 */
export function validateLegend(legend?: LegendOptionsBase | VizLegendOptions) {
  if (legend === undefined) {
    // undefined is valid since this is how legend is hidden by default
    return true;
  }
  if (!isValidLegendPosition(legend.position)) {
    return false;
  }
  if (legend.mode && !isValidLegendMode(legend.mode)) {
    return false;
  }
  if (legend.size && !isValidLegendSize(legend.size)) {
    return false;
  }

  return true;
}

export const legendSortOrderSchema = z.enum(['Asc', 'Desc']);

export const legendSortSchema = z.object({
  sort_by: z.string(),
  sort_order: legendSortOrderSchema,
});

// Note: explicitly defining different options for the legend spec and
// legend component that extend from some common options, so we can allow the
// component and the spec to diverge in some upcoming work.
export interface LegendSpecOptions extends z.TypeOf<typeof legendSpecOptionsSchema> {}
export const legendSpecOptionsSchema = legendOptionsBaseSchema.extend({
  values: z.array(aggregationsSchema).optional(),
  sort: z.array(legendSortSchema).optional(),
});

export type VizLegendOptions = Omit<LegendSpecOptions, 'size'> & {
  size?: LegendSize;
};
